import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaFilter, FaEye, FaEyeSlash } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner';
import ProductForm from './ProductForm';

const ProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(10);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/admin/products', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!response.ok) {
throw new Error('Failed to fetch products');
}

        const data = await response.json();
        setProducts(data);

        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(product => product.category))];
        setCategories(uniqueCategories);
      } catch (error) {
        // Error will be handled by the global error handler
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  const handleEditProduct = product => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDeleteProduct = async productId => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
return;
}

    try {
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
throw new Error('Failed to delete product');
}

      setProducts(products.filter(product => product.id !== productId));
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handleToggleVisibility = async (productId, currentVisibility) => {
    try {
      const response = await fetch(`/api/admin/products/${productId}/visibility`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ visible: !currentVisibility }),
      });

      if (!response.ok) {
throw new Error('Failed to update product visibility');
}

      // Update local state
      setProducts(
        products.map(product =>
          product.id === productId ? { ...product, visible: !currentVisibility } : product,
        ),
      );
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
return;
}

    if (!window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) {
      return;
    }

    try {
      const response = await fetch('/api/admin/products/bulk-delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ productIds: selectedProducts }),
      });

      if (!response.ok) {
throw new Error('Failed to delete products');
}

      setProducts(products.filter(product => !selectedProducts.includes(product.id)));
      setSelectedProducts([]);
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handleSaveProduct = async productData => {
    try {
      let url = '/api/admin/products';
      let method = 'POST';

      if (editingProduct) {
        url = `/api/admin/products/${editingProduct.id}`;
        method = 'PUT';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
throw new Error('Failed to save product');
}

      const savedProduct = await response.json();

      if (editingProduct) {
        setProducts(
          products.map(product => (product.id === editingProduct.id ? savedProduct : product)),
        );
      } else {
        setProducts([...products, savedProduct]);
      }

      setShowForm(false);
      setEditingProduct(null);
    } catch (error) {
      // Error will be handled by the global error handler
    }
  };

  const handleSelectProduct = productId => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(product => product.id));
    }
  };

  // Filter and search products
  const filteredProducts = products.filter(product => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = filterCategory === 'all' || product.category === filterCategory;

    return matchesSearch && matchesCategory;
  });

  // Pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  const renderPagination = () => {
    const pageNumbers = [];

    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>,
      );
    }

    return (
      <div className='pagination'>
        <button
          className='pagination-btn'
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {pageNumbers}

        <button
          className='pagination-btn'
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className='admin-products'>
      {showForm ? (
        <ProductForm
          product={editingProduct}
          categories={categories}
          onSave={handleSaveProduct}
          onCancel={() => {
            setShowForm(false);
            setEditingProduct(null);
          }}
        />
      ) : (
        <>
          <div className='products-header'>
            <div className='products-filters'>
              <div className='search-bar'>
                <FaSearch />
                <input
                  type='text'
                  placeholder='Search products...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              <div className='category-filter'>
                <FaFilter />
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
                  <option value='all'>All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className='products-actions'>
              <button className='btn-add' onClick={handleAddProduct}>
                <FaPlus /> Add Product
              </button>

              {selectedProducts.length > 0 && (
                <button className='btn-delete' onClick={handleBulkDelete}>
                  <FaTrash /> Delete Selected ({selectedProducts.length})
                </button>
              )}
            </div>
          </div>

          {currentProducts.length === 0 ? (
            <div className='no-products'>
              <p>No products found matching your criteria.</p>
              <button className='btn-add' onClick={handleAddProduct}>
                <FaPlus /> Add New Product
              </button>
            </div>
          ) : (
            <>
              <div className='products-table'>
                <table>
                  <thead>
                    <tr>
                      <th>
                        <input
                          type='checkbox'
                          checked={selectedProducts.length === filteredProducts.length}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Stock</th>
                      <th>Visible</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentProducts.map(product => (
                      <tr key={product.id}>
                        <td>
                          <input
                            type='checkbox'
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                          />
                        </td>
                        <td>
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className='product-thumbnail'
                          />
                        </td>
                        <td>{product.name}</td>
                        <td>{product.category}</td>
                        <td>${product.price.toFixed(2)}</td>
                        <td>{product.stock}</td>
                        <td>
                          <button
                            className={`visibility-btn ${product.visible ? 'visible' : 'hidden'}`}
                            onClick={() => handleToggleVisibility(product.id, product.visible)}
                          >
                            {product.visible ? <FaEye /> : <FaEyeSlash />}
                          </button>
                        </td>
                        <td>
                          <div className='table-actions'>
                            <button className='btn-edit' onClick={() => handleEditProduct(product)}>
                              <FaEdit />
                            </button>

                            <button
                              className='btn-delete'
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {renderPagination()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductsManagement;
